import { useEventListener, useIdle } from '@vueuse/core';
import { watch } from 'vue';
import type { Router } from 'vue-router';
import { stringifyQueryString } from '~/utils/string';

export const RENEWAL_LEAD_SECONDS = 5 * 60;
export const IDLE_MILLISECONDS = 60 * 1000;
export const RENEWAL_LOCK_MILLISECONDS = 60 * 1000;
const JITTER_MILLISECONDS = 30 * 1000;
const EDITABLE_SELECTOR =
    'input:not([type="button"]):not([type="checkbox"]):not([type="radio"]):not([type="reset"]):not([type="submit"]), textarea, select, [contenteditable]:not([contenteditable="false"])';
const MODIFIER_KEYS = new Set(['Alt', 'AltGraph', 'Control', 'Meta', 'Shift']);

// The browser holds one SAML state cookie for every team, so one round trip at a time.
export const RENEWING_KEY = 'voxie:sso-renewing';
export const expiresAtKey = (teamId: number): string => `voxie:sso-expires-at:${teamId}`;
export const renewedForKey = (teamId: number): string => `voxie:sso-renewed-for:${teamId}`;

const storedNumber = (key: string): number | null => {
    const value = Number(localStorage.getItem(key));

    return Number.isFinite(value) && value > 0 ? value : null;
};

const nowSeconds = (): number => Math.floor(Date.now() / 1000);

const currentPath = (): string => `${window.location.pathname}${window.location.search}${window.location.hash}`;

const editing = (): boolean =>
    document.activeElement instanceof HTMLElement && document.activeElement.matches(EDITABLE_SELECTOR);

export const useSsoRenewal = (router: Router): void => {
    const auth = Spark.state.user?.auth;
    const teamId = Spark.state.currentTeam?.id;
    const initialExpiresAt = teamId === undefined ? undefined : auth?.sso_expires_at[teamId];

    if (
        Spark.isImpersonating ||
        auth?.method !== 'sso' ||
        teamId === undefined ||
        initialExpiresAt === undefined ||
        Spark.ownsTeam
    ) {
        return;
    }

    const membership = Spark.state.teams.find((team) => team.id === teamId);

    if (!membership?.require_sso || membership.sso_protocol === 'oidc' || !membership.sso_login_url) {
        return;
    }

    const loginUrl = membership.sso_login_url;
    const keys = { expiresAt: expiresAtKey(teamId), renewedFor: renewedForKey(teamId) };
    let expiresAt = initialExpiresAt;
    // A page the person has clicked or typed in may hold unsaved work, which
    // only a navigation away from it may discard.
    let interacted = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const { idle } = useIdle(IDLE_MILLISECONDS);

    const due = (): boolean => nowSeconds() >= expiresAt - RENEWAL_LEAD_SECONDS && nowSeconds() < expiresAt;

    const worthwhile = (): boolean => {
        const renewedFor = storedNumber(keys.renewedFor);

        return renewedFor === null || expiresAt - renewedFor >= RENEWAL_LEAD_SECONDS;
    };

    const renewalInFlight = (): boolean => {
        const since = storedNumber(RENEWING_KEY);

        return since !== null && Date.now() - since < RENEWAL_LOCK_MILLISECONDS;
    };

    const renew = (intended: string, leaving = false): boolean => {
        if (!due() || !worthwhile() || renewalInFlight() || editing() || (interacted && !leaving)) {
            return false;
        }

        localStorage.setItem(RENEWING_KEY, String(Date.now()));
        localStorage.setItem(keys.renewedFor, String(expiresAt));
        window.location.assign(`${loginUrl}?${stringifyQueryString({ link: 1, renewal: 1, intended })}`);

        return true;
    };

    const renewIfQuiet = (): void => {
        if (!document.hidden && !idle.value) {
            return;
        }

        if (!renew(currentPath()) && due() && renewalInFlight()) {
            clearTimeout(timer);
            timer = setTimeout(renewIfQuiet, RENEWAL_LOCK_MILLISECONDS);
        }
    };

    const schedule = (): void => {
        clearTimeout(timer);
        timer = setTimeout(
            renewIfQuiet,
            Math.max(0, (expiresAt - RENEWAL_LEAD_SECONDS) * 1000 - Date.now()) + Math.random() * JITTER_MILLISECONDS
        );
    };

    const markInteracted = (): void => {
        interacted = true;
    };

    localStorage.setItem(keys.expiresAt, String(expiresAt));

    if (worthwhile()) {
        localStorage.removeItem(keys.renewedFor);
    }

    useEventListener(
        document,
        'pointerdown',
        (event: PointerEvent) => {
            if (event.pointerType !== 'touch') {
                markInteracted();
            }
        },
        { capture: true, passive: true }
    );
    // A touch the browser turns into a scroll ends in pointercancel, never pointerup.
    useEventListener(
        document,
        'pointerup',
        (event: PointerEvent) => {
            if (event.pointerType === 'touch') {
                markInteracted();
            }
        },
        { capture: true, passive: true }
    );
    useEventListener(
        document,
        'keydown',
        (event: KeyboardEvent) => {
            if (!MODIFIER_KEYS.has(event.key)) {
                markInteracted();
            }
        },
        { capture: true, passive: true }
    );
    router.beforeEach((to, from) => {
        if (renew(to.fullPath, to.path !== from.path)) {
            return false;
        }
    });
    router.afterEach((to, from, failure) => {
        if (failure === undefined && to.path !== from.path) {
            interacted = false;
        }
    });
    useEventListener(window, 'hashchange', () => {
        renew(currentPath(), true);
        interacted = false;
    });
    // An unloading document also reports itself hidden; its timers never run.
    useEventListener(document, 'visibilitychange', () => {
        setTimeout(renewIfQuiet, 0);
    });
    useEventListener(window, 'storage', (event: StorageEvent) => {
        const published = event.key === keys.expiresAt ? storedNumber(keys.expiresAt) : null;

        if (published !== null && published > expiresAt) {
            expiresAt = published;
            schedule();
        }
    });
    watch(idle, renewIfQuiet);

    schedule();
    renew(currentPath());
};
